import React, { useCallback, useEffect, useState } from 'react';
import { Folder, RefreshCw } from 'lucide-react';
import { filingsApi, type FilingProposal } from '@/services/api/agentApi';
import { FilingCard } from './FilingCard';
import { Button } from '@/components/ui/button';

const STATUS_TABS = [
  { value: 'pending_review', label: 'Pending' },
  { value: 'approved', label: 'Filed' },
  { value: 'rejected', label: 'Dismissed' },
] as const;

export const FilingProposalsTab: React.FC = () => {
  const [status, setStatus] = useState<string>('pending_review');
  const [items, setItems] = useState<FilingProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await filingsApi.list(status);
      setItems(res.data ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { void refresh(); }, [refresh]);

  const onApprove = async (id: string) => { await filingsApi.approve(id); await refresh(); };
  const onReject = async (id: string) => { await filingsApi.reject(id); await refresh(); };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Folder className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Filings</h2>
          <span className="text-xs text-muted-foreground">
            Things the agent saw but wants you to confirm before filing
          </span>
        </div>
        <Button size="sm" variant="ghost" onClick={() => void refresh()} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="flex items-center gap-1 mb-4 border-b">
        {STATUS_TABS.map(t => (
          <button
            key={t.value}
            onClick={() => setStatus(t.value)}
            className={`px-3 py-1.5 text-sm border-b-2 transition-colors ${
              status === t.value
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="text-sm text-red-600 mb-3">Failed to load: {error}</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-muted-foreground py-12 text-center">
          No filing proposals in this view.
        </div>
      )}

      {items.map(f => (
        <FilingCard key={f.filing_id} filing={f} onApprove={onApprove} onReject={onReject} />
      ))}
    </div>
  );
};
